import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { userService } from "@/api/services"
import type { UserResponseDto } from "@/api/types/user.types"
import Sidebar from "@/components/Sidebar"
import WalletInfo from "@/components/WalletInfo"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Loader2, ArrowLeft, UserX, UserCheck } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

export default function UserProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<UserResponseDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false)

  useEffect(() => {
    if (id) {
      loadUser()
    }
  }, [id])

  const loadUser = async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const userData = await userService.getUserById(id)
      setUser(userData)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load user"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleActive = async () => {
    if (!id || !user) return

    setToggling(true)
    setError("")
    setSuccess("")
    setToggleDialogOpen(false)

    try {
      const updatedUser = await userService.toggleUserActive(id, !user.isActive)
      setUser(updatedUser)
      setSuccess(
        updatedUser.isActive
          ? "User has been activated successfully"
          : "User has been deactivated successfully"
      )
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to update user status"
      setError(errorMessage)
    } finally {
      setToggling(false)
    }
  }

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
          <div className="max-w-2xl">
            <Card className="p-6">
              <div className="text-center">
                <p className="text-destructive mb-4">{error || "User not found"}</p>
                <Button onClick={() => navigate("/users")} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Users
                </Button>
              </div>
            </Card>
          </div>
        </main>
      </div>
    )
  }

  const userName = `${user.firstName} ${user.lastName}`.trim()

  return (
    <div className="flex">
      <Sidebar />
      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/users")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Users
            </Button>
            <h1 className="text-3xl font-bold text-foreground">User Profile</h1>
          </div>

          {/* Success message */}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* User Info (Read-only) */}
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">User Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="text-foreground font-medium">{user.email}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">User Type</Label>
                <p className="text-foreground font-medium">{user.userType}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">First Name</Label>
                <p className="text-foreground font-medium">{user.firstName || "—"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Last Name</Label>
                <p className="text-foreground font-medium">{user.lastName || "—"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Phone Number</Label>
                <p className="text-foreground font-medium">{user.phoneNumber || "—"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Address</Label>
                <p className="text-foreground font-medium">{user.address || "—"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">City</Label>
                <p className="text-foreground font-medium">{user.city || "—"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Status</Label>
                <p className="text-foreground font-medium">
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      user.isActive
                        ? "bg-success/10 text-success"
                        : "bg-destructive/10 text-destructive"
                    }`}
                  >
                    {user.isActive ? "Active" : "Inactive"}
                  </span>
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">Email Verified</Label>
                <p className="text-foreground font-medium">
                  {user.emailVerifiedAt ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">Created At</Label>
                <p className="text-foreground font-medium">
                  {new Date(user.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">Updated At</Label>
                <p className="text-foreground font-medium">
                  {new Date(user.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Card>

          {/* Wallet Information */}
          {id && <WalletInfo userId={id} />}

          {/* Actions */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Actions</h2>
            <div className="flex items-center gap-4">
              <Dialog open={toggleDialogOpen} onOpenChange={setToggleDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant={user.isActive ? "destructive" : "default"}
                    disabled={toggling}
                  >
                    {toggling ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {user.isActive ? "Deactivating..." : "Activating..."}
                      </>
                    ) : user.isActive ? (
                      <>
                        <UserX className="mr-2 h-4 w-4" />
                        Deactivate User
                      </>
                    ) : (
                      <>
                        <UserCheck className="mr-2 h-4 w-4" />
                        Activate User
                      </>
                    )}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {user.isActive ? "Deactivate User?" : "Activate User?"}
                    </DialogTitle>
                    <DialogDescription>
                      {user.isActive
                        ? `Are you sure you want to deactivate "${userName}"? The user will not be able to access the system.`
                        : `Are you sure you want to activate "${userName}"? The user will be able to access the system.`}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setToggleDialogOpen(false)}
                      disabled={toggling}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant={user.isActive ? "destructive" : "default"}
                      onClick={handleToggleActive}
                      disabled={toggling}
                    >
                      {toggling ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {user.isActive ? "Deactivating..." : "Activating..."}
                        </>
                      ) : user.isActive ? (
                        "Deactivate"
                      ) : (
                        "Activate"
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </Card>
        </div>
      </main>
    </div>
  )
}

